import { useEffect, useReducer, useRef, useState } from 'react'
import { WherebyRoomConnectionStatus } from '../../../../../../pure-js/enums/Whereby'
import { ONE_SECOND } from '../../../../../../pure-js/libs/Consts'

// A healthy reconnect brings sound and picture back within seconds; past this the call came back without them.
export const SILENT_CALL_GRACE_MS = 15 * ONE_SECOND // 15 s

const DROPPED_CALL_STATUSES = new Set<WherebyRoomConnectionStatus>([
  WherebyRoomConnectionStatus.DISCONNECTED,
  WherebyRoomConnectionStatus.RECONNECTING
])

type CallMediaParticipant = {
  isAudioEnabled?: boolean
  isVideoEnabled?: boolean
  stream?: MediaStream | null
}

// Someone has their microphone or camera on, yet nobody's sound or picture reaches this browser.
export const isCallSilent = (remoteParticipants: CallMediaParticipant[]): boolean => {
  const sendingParticipants = remoteParticipants.filter(
    (participant) => participant.isAudioEnabled || participant.isVideoEnabled
  )

  return sendingParticipants.length > 0 && sendingParticipants.every((participant) => !participant.stream)
}

interface SilentCallParams {
  connectionStatus?: WherebyRoomConnectionStatus
  remoteParticipants: CallMediaParticipant[]
}

// Back from a drop, the SDK lists everyone again and adds their sound and picture as it reaches them.
// True once that has happened for nobody within the grace, until it does or the call drops again.
export const useIsCallSilentAfterReconnect = ({ connectionStatus, remoteParticipants }: SilentCallParams): boolean => {
  const hasDroppedRef = useRef(false)
  const [reconnectCount, setReconnectCount] = useState(0)
  const [isGraceOver, setIsGraceOver] = useState(false)
  const isConnected = connectionStatus === WherebyRoomConnectionStatus.CONNECTED
  const isSilent = isCallSilent(remoteParticipants)

  useEffect(() => {
    if (connectionStatus && DROPPED_CALL_STATUSES.has(connectionStatus)) hasDroppedRef.current = true
    const isBackFromDrop = isConnected && hasDroppedRef.current
    if (!isBackFromDrop) return

    hasDroppedRef.current = false
    setIsGraceOver(false)
    setReconnectCount((count) => count + 1)
  }, [connectionStatus, isConnected])

  useEffect(() => {
    if (!reconnectCount) return

    const timeoutId = window.setTimeout(() => setIsGraceOver(true), SILENT_CALL_GRACE_MS)
    return () => window.clearTimeout(timeoutId)
  }, [reconnectCount])

  // Sound or picture is back, so there is nothing to offer until the next drop.
  useEffect(() => {
    if (isGraceOver && !isSilent) setIsGraceOver(false)
  }, [isGraceOver, isSilent])

  return isConnected && isGraceOver && isSilent
}

// A new key mounts a new Whereby store for the room: a connection from scratch, as a page reload gives.
export const useWherebyConnectionKey = (roomUrl?: string) => {
  const [generation, startFreshConnection] = useReducer((current: number) => current + 1, 0)

  return { connectionKey: `${roomUrl ?? ''}:${generation}`, startFreshConnection }
}
